// One reference image on a node (Info > Images): a visual prompt + the
// uploaded image URL. Max 5 per node.
export interface NodeImage {
	prompt: string;
	url: string;
}

// A named graph (Mission). Nodes belong to a graph only via reachability
// from its root; orphaned subtrees get a new GraphDoc or are rejoined.
export interface GraphDoc {
	id: string;
	name: string;
	root: string;
}

export interface GNode {
	id: string;
	shape: string;
	title: string;
	// Room number / number-letter combination (e.g. "2B"). In Foundry this
	// becomes the journal entry dragged onto the map.
	ident: string;
	// Free-form tags; the Graph Builder offers a tag cloud across all nodes.
	tags: string[];
	// Itemizer glyph name (e.g. "pistol-gun.svg"). Empty = fall back to the
	// subtype default glyph, then the type icon.
	glyph: string;
	// Optional separate colour for the glyph (null = use the node colour).
	glyphColor: string | null;
	// Reference images (Info > Images), max 5.
	images: NodeImage[];
	type: string;
	subType: string;
	info: string;        // GM-only notes (secrets, mechanics, contingencies)
	readAloud: string;   // Tier 1 read-aloud description (on entry)
	environment: string; // environmental state (lighting, gravity, hazards)
	color: string;
	svgUrl: string;
	svgData?: string;
	textX: number;
	textY: number;
	// Persisted Map-mode layout position (null = not yet placed). Written only
	// in the full-graph Map view; Focus view ignores it.
	x: number | null;
	y: number | null;
	parents: string[];
	children: string[];
}

export interface GraphNode extends GNode {
	parentNodes: GraphNode[];
	childNodes: GraphNode[];
}

export class GraphNodeUtil {

	static defaultNode(): GraphNode {
		return {
			id: '-1',
			shape: 'circle',
			title: '',
			ident: '',
			tags: [],
			glyph: '',
			glyphColor: null,
			images: [],
			color: '#51ff00',
			svgUrl: '',
			svgData: '',
			textX: 0.1,
			textY: 0.5,
			x: null,
			y: null,
			type: '',
			subType: '',
			info: '',
			readAloud: '',
			environment: '',
			parents: [],
			children: [],
			parentNodes: [],
			childNodes: [],
		}
	}

	static graphNodeFromNode(node: GNode): GraphNode {
		return {
			id: node.id,
			shape: node.shape,
			title: node.title,
			ident: node.ident ?? '',
			tags: node.tags ?? [],
			glyph: node.glyph ?? '',
			glyphColor: node.glyphColor ?? null,
			images: node.images ?? [],
			type: node.type,
			subType: node.subType,
			info: node.info ?? '',
			readAloud: node.readAloud ?? '',
			environment: node.environment ?? '',
			color: node.color,
			svgUrl: node.svgUrl,
			svgData: node.svgData,
			textX: node.textX,
			textY: node.textY,
			x: node.x ?? null,
			y: node.y ?? null,
			parents: node.parents,
			children: node.children,
			parentNodes: [],
			childNodes: []
		}
	}
  
	static graphNodeToNode(node: GraphNode): GNode {
		return {
			id: node.id,
			shape: node.shape,
			title: node.title,
			ident: node.ident ?? '',
			tags: node.tags ?? [],
			glyph: node.glyph ?? '',
			glyphColor: node.glyphColor ?? null,
			images: node.images ?? [],
			type: node.type,
			subType: node.subType,
			info: node.info ?? '',
			readAloud: node.readAloud ?? '',
			environment: node.environment ?? '',
			color: node.color,
			svgUrl: node.svgUrl,
			svgData: node.svgData,
			textX: node.textX,
			textY: node.textY,
			x: node.x ?? null,
			y: node.y ?? null,
			parents: node.parents,
			children: node.children
		}
	  }
  
	  static isRealized(node: GraphNode) {
		  return node.parents.length == node.parentNodes.length && node.children.length == node.childNodes.length;
	  }
}
